import { indicateProgress } from 'api'
import { getCurrentUserSaga } from 'auth/sagas'
import { userSelector } from 'auth/selectors'
import { saveAs } from 'file-saver'
import { call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import * as creditCardApi from 'credit-cards/nAmApi'
import {
    setSubscription,
    setSubscriptionInvoices,
    setSubscriptionsHistory,
    setWaitList,
    setSubscriptionReceipts,
} from 'dashboard-subscriptions/actions'
import * as constants from 'dashboard-subscriptions/constants'
import * as nAmApi from 'dashboard-subscriptions/nAmApi'
import { parkerDataForAssignSelector, subscriptionSelector } from 'dashboard-subscriptions/selectors'
import { preferredLocaleSelector } from 'wordpress/selectors'

export function* getSubscriptionsHistorySaga() {
    yield takeLatest(constants.GET_SUBSCRIPTIONS_HISTORY, function* () {
        try {
            const subscriptions = yield indicateProgress(['subscriptionHistory'], function* () {
                const { token, accountKey } = yield select(userSelector)
                const resultedSubscriptions = yield call(nAmApi.getSubscriptionHistory, accountKey, token)
                yield put(setSubscriptionsHistory(resultedSubscriptions))

                return resultedSubscriptions
            })

            yield call(getAndUpdateSubscriptionsBalance, subscriptions)
        } catch (e) {}
    })
}

export function* getWaitListSaga() {
    const waitList = yield indicateProgress(['waitList'], function* () {
        const { token, accountKey } = yield select(userSelector)
        const waitList = yield call(nAmApi.getWaitList, accountKey, token)

        const filteredWaitList = waitList.filter(({ state }) =>
            [constants.WAIT_LIST_PENDING, constants.WAIT_LIST_REQUESTED].includes(state)
        )

        yield put(setWaitList(filteredWaitList))

        return filteredWaitList
    })

    yield call(getAndUpdateWaitListLocations, waitList)
}

export function* deleteWaitListItemSaga({ waitListId, locale }) {
    const { token } = yield select(userSelector)

    try {
        yield call(nAmApi.deleteWaitListItem, waitListId, token, locale)
        return yield call(getWaitListSaga)
    } catch (error) {
        return null
    }
}

const CHUNK_SIZE = 3

//TODO getting balances need to be refactored as it is triggering multiple rerenders
export function* getAndUpdateSubscriptionsBalance(subscriptions) {
    try {
        const updatedSubscriptions = [...subscriptions]
        if (subscriptions.length > 0) {
            let index = 0
            while (index < subscriptions.length) {
                const subscription = subscriptions[index]
                const updatedSubscription = { ...subscription, balance: null }

                updatedSubscription.balance = yield call(getSubscriptionBalance, subscription)

                updatedSubscriptions[index] = updatedSubscription

                index++

                if (index % CHUNK_SIZE === 0) {
                    yield put(setSubscriptionsHistory([...updatedSubscriptions]))
                }
            }

            yield put(setSubscriptionsHistory(updatedSubscriptions))
        }
    } catch (e) {
        return []
    }
}

export function* getAndUpdateWaitListLocations(waitList) {
    const updatedWaitList = [...waitList]
    if (waitList.length > 0) {
        let index = 0
        while (index < waitList.length) {
            const item = waitList[index]
            const updatedItem = { ...item, locationName: null }

            updatedItem.locationName = yield call(getWaitListItemLocation, item)

            updatedWaitList[index] = updatedItem

            index++

            if (index % CHUNK_SIZE === 0) {
                yield put(setWaitList([...updatedWaitList]))
            }
        }

        yield put(setWaitList(updatedWaitList))
    }
}

export function* getSubscriptionBalance(subscription) {
    try {
        const { token } = yield select(userSelector)
        const zaccountId = subscription.extended?.fieldMap?.zuoraAcctMapping?.fieldList[0]?.fieldMap?.zaccountId?.value

        if (zaccountId) {
            const balance = yield call(nAmApi.getSubscriptionBalance, zaccountId, token)
            return balance?.balance
        }

        return null
    } catch (e) {
        return null
    }
}

export function* getWaitListItemLocation(item) {
    const { token } = yield select(userSelector)

    const itemWithLocation = yield call(nAmApi.getWaitListItem, item.waitListId, token)
    return itemWithLocation.locationName
}

export function* getSubscriptionAfterUpdate(subscriptionId) {
    try {
        const { token } = yield select(userSelector)

        const updatedSubscription = yield call(nAmApi.getOneSubscription, subscriptionId, token)
        updatedSubscription.balance = yield call(getSubscriptionBalance, updatedSubscription)
        yield put(setSubscription(updatedSubscription))
    } catch (e) {
        return null
    }
}

export function* updateSubscriptionSaga() {
    yield takeEvery(constants.UPDATE_SUBSCRIPTION, function* ({ data }) {
        try {
            yield indicateProgress([constants.UPDATE_SUBSCRIPTION_PROGRESS], function* () {
                const { token } = yield select(userSelector)
                yield call(nAmApi.updateSubscription, token, data)
                yield call(getSubscriptionAfterUpdate, data.id)
            })
        } catch (e) {}
    })
}

export function* getSubscriptionInvoicesSaga({ subscriptionId }) {
    try {
        yield indicateProgress(['subscriptionInvoices'], function* () {
            const { token } = yield select(userSelector)
            const invoices = yield call(nAmApi.getSubscriptionInvoices, subscriptionId, token)
            const filteredInvoices = invoices.filter(i => i.status === constants.INVOICE_TYPE_POSTED)
            yield put(setSubscriptionInvoices(filteredInvoices))
        })
    } catch (e) {
        yield put(setSubscriptionInvoices([]))
    }
}

export function* getSubscriptionReceiptsSaga({ subscriptionId }) {
    try {
        yield indicateProgress(['subscriptionReceipts'], function* () {
            const { token } = yield select(userSelector)
            const receipts = yield call(nAmApi.getSubscriptionReceipts, subscriptionId, token)
            yield put(setSubscriptionReceipts(receipts))
        })
    } catch (e) {
        yield put(setSubscriptionReceipts([]))
    }
}

export function* payInvoiceSaga({ data }) {
    try {
        yield indicateProgress([constants.INVOICE_PAYMENT_PROGRESS], function* () {
            const { token } = yield select(userSelector)

            yield call(nAmApi.payInvoice, token, {
                paymentRequest: {
                    ...data,
                    paymentMethodType: constants.PAYMENT_METHOD_TYPE,
                },
            })

            yield call(getSubscriptionInvoicesSaga, { subscriptionId: data.subscriptionId })
            const subscription = yield select(subscriptionSelector(data.subscriptionId))
            subscription.balance = yield call(getSubscriptionBalance, subscription)
            yield put(setSubscription(subscription))
        })
    } catch (e) {}
}

export function* downloadInvoiceSaga({ invoiceId }) {
    try {
        const { token } = yield select(userSelector)
        const invoice = yield call(nAmApi.downloadInvoice, invoiceId, token)
        saveAs(invoice, 'invoice.pdf')
    } catch (e) {}
}

export function* downloadReceiptSaga({ subscriptionId, paymentNumber }) {
    try {
        const { token } = yield select(userSelector)
        const lang = yield select(preferredLocaleSelector)
        const receipt = yield call(nAmApi.downloadReceipt, subscriptionId, paymentNumber, lang.toUpperCase(), token)
        saveAs(receipt, `receipt_${paymentNumber}.pdf`)
    } catch (e) {}
}

export function* assignParkerSaga({ data }) {
    try {
        yield indicateProgress([constants.ASSIGN_PARKER_PROGRESS], function* () {
            const { token } = yield select(userSelector)
            const members = yield select(parkerDataForAssignSelector(data))

            const currentMember = members.find(member => member.id === data.memberId)
            const submitData = {
                chargeId: null,
                chargeReason: null,
                members: [currentMember],
            }

            yield call(nAmApi.assignParker, submitData, token, data.subscriptionId, data.selfService)
            yield call(getSubscriptionAfterUpdate, data.subscriptionId)
        })
    } catch (e) {}
}

export function* removeParkerSaga({ subscriptionId, memberId }) {
    try {
        yield indicateProgress([constants.REMOVE_PARKER_PROGRESS], function* () {
            const { token } = yield select(userSelector)
            const subscription = yield select(subscriptionSelector(subscriptionId))
            // eslint-disable-next-line no-undef
            const subscriptionCopy = _.cloneDeep(subscription)

            const submitData = subscriptionCopy?.members?.map(m => {
                if (m.id === memberId) {
                    delete m.firstName
                    delete m.lastName
                    delete m.email
                    delete m.extended?.fieldMap?.vehicles
                    delete m.extended?.fieldMap?.selfService
                    m.unoccupied = true
                }

                return m
            })
            yield call(
                nAmApi.assignParker,
                { chargeId: null, chargeReason: null, members: submitData },
                token,
                subscriptionId,
                false
            )
            yield call(getSubscriptionAfterUpdate, subscriptionId)
        })
    } catch (e) {}
}

export function* setPadDataSaga({ data }) {
    try {
        yield call(indicateProgress, { names: [constants.SAVE_PAD_DATA_PROGRESS] }, function* () {
            const { token, accountKey } = yield select(userSelector)

            const cardData = {
                achAccountName: data.nameOnCard,
                ...data,
            }

            yield call(creditCardApi.savePadData, accountKey, token, cardData)
            yield getCurrentUserSaga()
        })
    } catch (ex) {}
}

export function* rootSaga() {
    yield fork(getSubscriptionsHistorySaga)
    yield fork(updateSubscriptionSaga)
    yield takeLatest(constants.PAY_INVOICE, payInvoiceSaga)
    yield takeLatest(constants.GET_SUBSCRIPTION_INVOICES, getSubscriptionInvoicesSaga)
    yield takeLatest(constants.GET_SUBSCRIPTION_RECEIPTS, getSubscriptionReceiptsSaga)
    yield takeLatest(constants.DOWNLOAD_INVOICE, downloadInvoiceSaga)
    yield takeLatest(constants.DOWNLOAD_SUBSCRIPTION_RECEIPT, downloadReceiptSaga)
    yield takeLatest(constants.ASSIGN_PARKER, assignParkerSaga)
    yield takeLatest(constants.REMOVE_PARKER, removeParkerSaga)
    yield takeLatest(constants.SAVE_PAD_DATA, setPadDataSaga)
    yield takeLatest(constants.GET_WAIT_LIST, getWaitListSaga)
    yield takeLatest(constants.DELETE_WAIT_LIST_ITEM, deleteWaitListItemSaga)
}
